<template>
    <v-list nav dense>
        <v-subheader class="pl-0">
            <v-hover v-slot:default="{ hover }">
                <v-btn small text color="grey darken-1" class="text-caption" @click="$emit('update:expanded', !expanded)">
                    SPACE SETTINGS
                    <v-icon v-if="hover" right small>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-hover>
        </v-subheader>
        <v-expand-transition>
            <div v-show="expanded">
                <SpaceMenu :collapsed="collapsed" />

                <template v-if="isConfigSpaceSelected">
                    <v-list-item
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-general', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-cog-outline</v-icon>
                                </template>
                                <span>General</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>General</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-if="isSpaceAdmin"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-users', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-account-group-outline</v-icon>
                                </template>
                                <span>Users</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Users</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="!collapsed" class="my-0">
                            <SpaceInviteDialog ref="drawerSpaceInviteDialog">
                                <template v-slot:activator>
                                    <v-btn text small class="px-2" min-width="0" @click.stop.prevent="openSpaceInviteDialog()">
                                        <v-icon small>mdi-plus</v-icon>
                                    </v-btn>
                                </template>
                            </SpaceInviteDialog>
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item
                        v-if="isSpaceAdmin"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-resource', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-sitemap-outline</v-icon>
                                </template>
                                <span>Resource mapping</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Resource mapping</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-if="isSpaceAdmin"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-features', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-puzzle-outline</v-icon>
                                </template>
                                <span>Additional services</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Additional services</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-secrets', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-lock-outline</v-icon>
                                </template>
                                <span>Secrets</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Secrets</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="!collapsed && isSpaceAdmin && !isSpaceArchived" class="my-0">
                            <SecretCreateDialog :level="2" :entity-id="activeOrgSpaceId">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>mdi-plus</v-icon>
                                    </v-btn>
                                </template>
                            </SecretCreateDialog>
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-lfs', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-server-outline</v-icon>
                                </template>
                                <span>Large file storage</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Large file storage</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="!collapsed && isSpaceAdmin" class="my-0">
                            <SpaceAttachStorageDialog :space-id="activeOrgSpaceId">
                                <template v-slot:activator="{ on, attrs, open }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent="open">
                                        <v-icon small>mdi-connection</v-icon>
                                    </v-btn>
                                </template>
                            </SpaceAttachStorageDialog>
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item
                        v-if="switchCloudEnabled"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        :to="{ name: 'config-space-cloud-storage', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-cloud-outline</v-icon>
                                </template>
                                <span>External storage</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>External storage</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="!collapsed && isSpaceAdmin" class="my-0">
                            <SpaceAttachExternalStorageDialog :space-id="activeOrgSpaceId">
                                <template v-slot:activator="{ on, attrs, open }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent="open">
                                        <v-icon small>mdi-connection</v-icon>
                                    </v-btn>
                                </template>
                            </SpaceAttachExternalStorageDialog>
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item
                        v-if="isSpaceAdmin"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        exact
                        :to="{ name: 'config-space-file-quotas', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-chart-donut</v-icon>
                                </template>
                                <span>Quotas and usage</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Quotas and usage</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="showStudentQuotasNav"
                        :class="{ 'ml-2 drawer-active-item': !collapsed }"
                        active-class="primary--text"
                        exact
                        :to="{ name: 'config-space-credit-quotas', params: { oid: currentOrgOid, sid: activeOrgSpaceId } }">
                        <v-list-item-icon>
                            <v-tooltip right :disabled="!collapsed">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on">mdi-school-outline</v-icon>
                                </template>
                                <span>Instance credit quotas</span>
                            </v-tooltip>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Instance credit quotas</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </template>
            </div>
        </v-expand-transition>
    </v-list>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'

const SpaceMenu = () => import('@/components/SpaceMenu.vue')
const SecretCreateDialog = () => import('@/components/SecretCreateDialog.vue')
const SpaceAttachStorageDialog = () => import('@/modules/space/components/SpaceAttachStorageDialog.vue')
const SpaceAttachExternalStorageDialog = () => import('@/modules/space/components/SpaceAttachExternalStorageDialog.vue')
const SpaceInviteDialog = () => import('@/modules/space/components/SpaceInviteDialog.vue')

export default {
    name: 'DrawerSpaceSettingsSection',
    mixins: [enumsData],
    components: {
        SpaceMenu,
        SecretCreateDialog,
        SpaceAttachStorageDialog,
        SpaceAttachExternalStorageDialog,
        SpaceInviteDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        expanded: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapState(['userOrgs', 'currentOrgData']),
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'isSpaceArchived', 'currentSpaceType', 'currentSpaceData']),
        currentOrgOid() {
            return this.$route.params.oid || this.currentOrgData?.oid || this.userOrgs[0]?.oid
        },
        activeOrgSpaceId() {
            return this.$route.params.sid
        },
        isConfigSpaceSelected() {
            return Boolean(this.$route.params.sid)
        },
        showStudentQuotasNav() {
            return this.isSpaceAdmin && this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE && Boolean(this.currentSpaceData?.beegfs_sync)
        },
        switchCloudEnabled() {
            return false
        }
    },
    methods: {
        openSpaceInviteDialog() {
            const dialog = this.$refs.drawerSpaceInviteDialog
            if (dialog && dialog.openDialog) {
                dialog.openDialog()
            }
        }
    }
}
</script>
